/** @format */

import type {ChartLabelList} from '../../model/charts';

import {MAX_PEAK_LABELS, MAX_PIT_LABELS} from '../../const';
import {PointName} from '../../model/common';

export interface FindExtremesOptions {
  data: ChartLabelList;
  maxPeakLabels?: number;
  maxPitLabels?: number;
}

/**
 * Найдёт экстремумы (минимумы/максимумы), а также первый и последний поинты
 * и вернёт новый ChartLabelList, где primary.show = true только у этих поинтов.
 */

const findExtremes = ({
  data,
  maxPitLabels = MAX_PIT_LABELS,
  maxPeakLabels = MAX_PEAK_LABELS,
}: FindExtremesOptions): ChartLabelList => {
  // Отфильтровать значения без подписей

  const points = Object.keys(data) as PointName[];

  // Найти последний key, где primary определён

  let lastDefined = points.length - 1;

  for (let i = 0; i < points.length; i++) {
    const point = points[i];
    if (data[point].primary === undefined) {
      lastDefined = i - 1;
      break;
    }
  }

  // Если значений меньше максимального, взять все

  let defaultValuesCount = 1;
  if (lastDefined !== 0) {
    defaultValuesCount++;
  }
  const maxValuesCount = defaultValuesCount + maxPitLabels + maxPeakLabels;

  if (points.length <= maxValuesCount) {
    return data;
  }

  // Выбрать середину массива и отсортировать по возрастанию значений

  const middle = points
    .filter((_, index) => index !== 0 && index !== lastDefined)
    .toSorted((a, b) => {
      const left = data[a].primary?.value ?? 0;
      const right = data[b].primary?.value ?? 0;
      return left - right;
    });

  // Выбрать первое значение, последнее, до двух самых маленьких и двух самых больших

  const defaultValues = [points[0], points[lastDefined], points[points.length - 1]];
  const smallest = middle.slice(0, maxPitLabels);
  const largest = middle.slice(-maxPeakLabels);
  const resultPointNames = new Set<PointName>(smallest.concat(largest).concat(defaultValues));

  const result: ChartLabelList = {};
  const resultPointNamesArray = Array.from(resultPointNames);

  for (const key of Object.keys(data)) {
    result[key] = {
      ...data[key],
      primary: {
        ...data[key].primary,
        show: resultPointNamesArray.includes(key),
      },
    };
  }

  return result;
};

export {findExtremes};
